import { useState } from 'react';
import type React from 'react';
import clsx from 'clsx';
import { InsightsSectionNav } from '../../weld-analytics/components/shared/InsightsSectionNav';

interface MLInsight {
  type: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | string;
  confidence?: number;
  title: string;
  description: string;
  affectedRecords?: number;
  details?: Record<string, any>;
  recommendation?: string;
  statisticalSignificance?: number;
  method?: string;
}

interface MLInsightSummary {
  totalRecords: number;
  rejectionRate: number | null;
  acceptedCount: number;
  rejectedCount: number;
  hasMixedData?: boolean;
  primaryDefectType: string;
  primaryDefectPercentage: number;
  keyFindings: {
    highSeverityPatterns: number;
    highSeverityCorrelations: number;
    criticalAnomalies: number;
  };
  overallRiskLevel: 'critical' | 'high' | 'medium' | 'low';
  naturalLanguageSummary: string;
  method?: string;
}

interface MLInsightsData {
  summary: MLInsightSummary;
  patterns: MLInsight[];
  correlations: MLInsight[];
  anomalies: MLInsight[];
  thresholds?: Record<string, any>;
  method?: string;
}

interface MLInsightsPanelProps {
  insights: MLInsightsData | null;
  isLoading?: boolean;
  /** Hide the section pill nav (parent owns a unified nav). */
  hideNav?: boolean;
  /** Controlled active section id (summary | patterns | correlations | anomalies). */
  activeSection?: string;
  onActiveSectionChange?: (id: string) => void;
  /** Show AI badge on each tab when nav is visible (standalone use). */
  showAiBadges?: boolean;
}

const SeverityBadge = ({ severity }: { severity?: string | null }) => {
  const colors = {
    critical: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    high: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    medium: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    low: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
  };

  const severityValue = severity || 'low';
  const severityKey = severityValue.toLowerCase() as keyof typeof colors;

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
        colors[severityKey] || colors.low
      )}
    >
      {severityValue.toUpperCase()}
    </span>
  );
};

// Helper function to format detail keys for display
const formatDetailKey = (key: string): string => {
  const keyMap: Record<string, string> = {
    defectType: 'Defect Type',
    occurrences: 'Occurrences',
    percentage: 'Percentage',
    oclockPosition: 'Position',
    pipeDiameter: 'Pipe Diameter',
    frequencyIncrease: 'Frequency Increase',
    condition: 'Condition',
    expectedOccurrences: 'Expected',
    weldNumber: 'Weld Number',
    defectLength: 'Defect Length',
    typicalMaxLength: 'Typical Max',
    measurement1: 'Measurement 1',
    measurement2: 'Measurement 2',
    relationship: 'Relationship',
    direction: 'Direction',
    sampleSize: 'Sample Size',
  };
  return keyMap[key] || key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, (str) => str.toUpperCase());
};

// Helper function to format detail values
const formatDetailValue = (key: string, value: any): string => {
  // Skip null/undefined values
  if (value === null || value === undefined) {
    return '';
  }
  
  // Skip technical fields that shouldn't be shown
  const technicalFields = ['method', 'clusterId', 'clusterSize', 'clusterCharacteristics', 'adaptiveThreshold', 'pValue', 'chiSquare', 'pearsonCorrelation', 'anomalyScore', 'variable1', 'variable2', 'observedCount', 'expectedCount', 'ratio'];
  if (technicalFields.includes(key)) {
    return '';
  }
  
  // Handle objects - don't show them
  if (typeof value === 'object' && !Array.isArray(value)) {
    return '';
  }
  
  // Format numbers
  if (typeof value === 'number') {
    if (key.includes('percentage') || key.includes('Percentage')) {
      return `${value.toFixed(1)}%`;
    }
    if (key.includes('length') || key.includes('Length') || key.includes('depth') || key.includes('Depth') || key.includes('height') || key.includes('Height')) {
      return `${value.toFixed(1)} mm`;
    }
    return value.toFixed(0);
  }
  
  return String(value);
};

const MLInsightCard = ({ insight, index }: { insight: MLInsight; index: number }) => {
  const [expanded, setExpanded] = useState(index < 3);

  // Filter out technical details and null values
  const userFriendlyDetails = insight.details 
    ? Object.entries(insight.details)
        .filter(([key, value]) => {
          // Filter out technical fields
          const technicalFields = ['method', 'clusterId', 'clusterSize', 'clusterCharacteristics', 'adaptiveThreshold', 'pValue', 'chiSquare', 'pearsonCorrelation', 'anomalyScore', 'variable1', 'variable2', 'observedCount', 'expectedCount', 'ratio'];
          if (technicalFields.includes(key)) return false;
          // Filter out null/undefined
          if (value === null || value === undefined) return false;
          // Filter out objects
          if (typeof value === 'object' && !Array.isArray(value)) return false;
          return true;
        })
        .map(([key, value]) => [formatDetailKey(key), formatDetailValue(key, value)])
        .filter(([_, value]) => value !== '') // Remove empty values
    : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div
        className="cursor-pointer p-4 hover:bg-slate-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {insight.severity && <SeverityBadge severity={insight.severity} />}
              {insight.confidence && (
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  {Math.round(insight.confidence * 100)}% confidence
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">{insight.title}</h3>
            <p className="text-sm text-slate-700 leading-relaxed">{insight.description}</p>
            {insight.affectedRecords && (
              <p className="mt-2 text-xs text-slate-500 font-medium">
                {insight.affectedRecords} case{insight.affectedRecords !== 1 ? 's' : ''} found
              </p>
            )}
          </div>
          <button 
            className="ml-4 text-slate-400 hover:text-slate-600 text-xl font-light"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
          {insight.recommendation && (
            <div className="bg-blue-50 border-l-4 p-3 rounded">
              <p className="text-xs font-semibold text-blue-900 mb-1">Recommended Action</p>
              <p className="text-sm text-blue-800 leading-relaxed">{insight.recommendation}</p>
            </div>
          )}
          {userFriendlyDetails.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Key Information
              </p>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                {userFriendlyDetails.map(([key, value], idx) => (
                  <div key={idx} className="bg-white p-2 rounded border border-slate-200">
                    <dt className="text-slate-600 text-xs mb-1">{key}</dt>
                    <dd className="font-semibold text-slate-900">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {insight.type === 'ml_anomaly' && (
            <div className="bg-amber-50 border-l-4 p-3 rounded">
              <p className="text-xs font-semibold text-amber-900 mb-1">What makes this unusual?</p>
              <p className="text-xs text-amber-800 leading-relaxed">
                This weld was flagged because its combination of characteristics (defect type, size, location, etc.) 
                differs significantly from typical welds in your dataset. This doesn't necessarily mean there's a problem, 
                but it's worth investigating to understand why this weld stands out.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ title, count, description }: { title: string; count?: number; description?: string }) => (
  <div className="mb-4">
    <div className="flex items-center gap-3">
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      {count !== undefined && count !== null && (
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
          {count}
        </span>
      )}
    </div>
    {description && <p className="mt-2 text-sm text-slate-600">{description}</p>}
  </div>
);

const FilterBar = ({ 
  severityFilter, 
  setSeverityFilter, 
  typeFilter, 
  setTypeFilter,
  availableSeverities,
  availableTypes,
  totalCount,
  filteredCount
}: {
  severityFilter: string;
  setSeverityFilter: (value: string) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
  availableSeverities: string[];
  availableTypes: string[];
  totalCount: number;
  filteredCount: number;
}) => (
  <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
    <div className="flex items-center gap-2">
      <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
      <span className="text-sm font-medium text-slate-700">Filters:</span>
    </div>
    
    <select
      value={severityFilter}
      onChange={(e) => setSeverityFilter(e.target.value)}
      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
    >
      <option value="all">All Severities</option>
      {availableSeverities.map(severity => (
        <option key={severity} value={severity}>
          {severity.charAt(0).toUpperCase() + severity.slice(1)}
        </option>
      ))}
    </select>

    <select
      value={typeFilter}
      onChange={(e) => setTypeFilter(e.target.value)}
      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
    >
      <option value="all">All Types</option>
      {availableTypes.map(type => (
        <option key={type} value={type}>{type}</option>
      ))}
    </select>

    {(severityFilter !== 'all' || typeFilter !== 'all') && (
      <>
        <button
          onClick={() => {
            setSeverityFilter('all');
            setTypeFilter('all');
          }}
          className="text-xs text-brand hover:text-brand/80 font-medium"
        >
          Clear Filters
        </button>
        <span className="text-xs text-slate-500">
          Showing {filteredCount} of {totalCount}
        </span>
      </>
    )}
  </div>
);

export const MLInsightsPanel = ({
  insights,
  isLoading,
  hideNav = false,
  activeSection: activeSectionProp,
  onActiveSectionChange,
  showAiBadges = true,
}: MLInsightsPanelProps) => {
  const [internalSection, setInternalSection] = useState<string>('summary');
  const activeSection = activeSectionProp ?? internalSection;
  const setActiveSection = onActiveSectionChange ?? setInternalSection;
  
  // Filter states
  const [patternSeverityFilter, setPatternSeverityFilter] = useState<string>('all');
  const [patternTypeFilter, setPatternTypeFilter] = useState<string>('all');
  const [correlationSeverityFilter, setCorrelationSeverityFilter] = useState<string>('all');
  const [correlationTypeFilter, setCorrelationTypeFilter] = useState<string>('all');
  const [anomalySeverityFilter, setAnomalySeverityFilter] = useState<string>('all');
  const [anomalyTypeFilter, setAnomalyTypeFilter] = useState<string>('all');

  // Helper to get SME-friendly type name from technical insight type
  const getReadableType = (type: string): string => {
    const lowerType = type.toLowerCase();
    
    // Welder-related
    if (lowerType.includes('welder')) return 'Welder';
    
    // Shift-related
    if (lowerType.includes('shift')) return 'Shift';
    
    // Root cause
    if (lowerType.includes('root')) return 'Root Cause';
    
    // Defect patterns
    if (lowerType.includes('defect')) return 'Defect Pattern';
    
    // Time/Date-based (instead of "Temporal")
    if (lowerType.includes('temporal') || lowerType.includes('trend')) return 'Date Trend';
    
    // Seasonal (user-friendly as is)
    if (lowerType.includes('seasonal')) return 'Monthly/Seasonal';
    
    // Location-based (o'clock position, weld location)
    if (lowerType.includes('location') || lowerType.includes('oclock') || lowerType.includes('position')) {
      return 'Location';
    }
    
    // Pipe-related
    if (lowerType.includes('pipe') || lowerType.includes('diameter')) return 'Pipe Size';
    
    // Measurements (length, depth, height)
    if (lowerType.includes('measurement') || lowerType.includes('length') || lowerType.includes('depth')) {
      return 'Defect Size';
    }
    
    // Cluster patterns (avoid "ML" terminology)
    if (lowerType.includes('cluster') || lowerType.includes('ml_')) {
      return 'Similar Defects';
    }
    
    // Anomalies
    if (lowerType.includes('anomaly') || lowerType.includes('unusual')) return 'Unusual';
    
    // Statistical patterns (avoid "adaptive statistical")
    if (lowerType.includes('statistical') || lowerType.includes('adaptive')) {
      return 'Recurring Pattern';
    }
    
    // Fallback: convert snake_case to Title Case, but avoid technical terms
    return type
      .replace(/_/g, ' ')
      .replace(/\bml\b/gi, '')
      .replace(/\badaptive\b/gi, '')
      .replace(/\bstatistical\b/gi, '')
      .trim()
      .split(' ')
      .filter(w => w.length > 0)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || 'Other';
  };

  // Get unique types and severities for filters
  const getUniqueTypes = (items: MLInsight[]) => {
    const types = new Set(items.map(item => getReadableType(item.type || 'unknown')));
    return Array.from(types).sort();
  };

  const getUniqueSeverities = (items: MLInsight[]) => {
    const severities = new Set(items.map(item => (item.severity || 'low').toLowerCase()));
    const order = ['critical', 'high', 'medium', 'low'];
    return Array.from(severities).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  };

  // Filter function
  const filterInsights = (items: MLInsight[], severityFilter: string, typeFilter: string) => {
    return items.filter(item => {
      const severityMatch = severityFilter === 'all' || (item.severity || 'low').toLowerCase() === severityFilter;
      const itemType = getReadableType(item.type || '');
      const typeMatch = typeFilter === 'all' || itemType === typeFilter;
      return severityMatch && typeMatch;
    });
  };

  // Apply filters
  const filteredPatterns = insights ? filterInsights(insights.patterns, patternSeverityFilter, patternTypeFilter) : [];
  const filteredCorrelations = insights ? filterInsights(insights.correlations, correlationSeverityFilter, correlationTypeFilter) : [];
  const filteredAnomalies = insights ? filterInsights(insights.anomalies, anomalySeverityFilter, anomalyTypeFilter) : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand border-r-transparent"></div>
          <p className="mt-4 text-sm text-slate-500">Generating ML-powered insights...</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-500">
        <p className="text-lg font-semibold text-slate-900">No ML Insights Available</p>
        <p className="mt-2 text-sm text-slate-500">
          Upload data to generate AI-powered insights and identify patterns in your weld data.
        </p>
      </div>
    );
  }

  const sections = [
    { id: 'summary', label: 'Summary', count: null },
    { id: 'patterns', label: 'Patterns', count: filteredPatterns.length },
    { id: 'correlations', label: 'Relationships', count: filteredCorrelations.length },
    { id: 'anomalies', label: 'Unusual Cases', count: filteredAnomalies.length },
  ];

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'summary':
        return (
          <div className="space-y-6">

            {/* Risk Level Card */}
            <div className="rounded-lg border-2 border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Overall Risk Assessment</h3>
                <SeverityBadge severity={insights.summary.overallRiskLevel} />
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <p className="text-xs text-slate-500">Rejection Rate</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.rejectionRate !== null
                      ? `${insights.summary.rejectionRate.toFixed(1)}%`
                      : 'N/A'}
                  </p>
                  {!insights.summary.hasMixedData && insights.summary.rejectedCount > 0 && (
                    <p className="text-xs text-slate-400">Defect-only dataset</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Records</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.totalRecords.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Primary Defect</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.primaryDefectType}
                  </p>
                  <p className="text-xs text-slate-400">
                    {insights.summary.primaryDefectPercentage.toFixed(1)}% of defects
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Key Findings</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {insights.summary.keyFindings.highSeverityPatterns +
                      insights.summary.keyFindings.highSeverityCorrelations +
                      insights.summary.keyFindings.criticalAnomalies}
                  </p>
                  <p className="text-xs text-slate-400">High-severity insights</p>
                </div>
              </div>
            </div>

            {/* Natural Language Summary */}
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Analysis Summary</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                {insights.summary.naturalLanguageSummary}
              </p>
            </div>
          </div>
        );

      case 'patterns':
        return (
          <div className="space-y-6">
            <SectionHeader
              title="Defect Patterns"
              count={filteredPatterns.length}
              description="Recurring patterns in defect occurrence that may indicate systematic issues"
            />
            <FilterBar
              severityFilter={patternSeverityFilter}
              setSeverityFilter={setPatternSeverityFilter}
              typeFilter={patternTypeFilter}
              setTypeFilter={setPatternTypeFilter}
              availableSeverities={getUniqueSeverities(insights.patterns)}
              availableTypes={getUniqueTypes(insights.patterns)}
              totalCount={insights.patterns?.length || 0}
              filteredCount={filteredPatterns.length}
            />
            {filteredPatterns.length > 0 ? (
              <div className="space-y-4">
                {filteredPatterns.map((pattern, idx) => (
                  <MLInsightCard key={idx} insight={pattern} index={idx} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                {insights.patterns.length > 0 
                  ? 'No patterns match the selected filters. Try adjusting your filters.'
                  : 'No significant patterns detected in the data.'}
              </p>
            )}
          </div>
        );

      case 'correlations':
        return (
          <div className="space-y-6">
            <SectionHeader
              title="Defect Relationships"
              count={filteredCorrelations.length}
              description="Strong relationships between defects and conditions that may help identify root causes"
            />
            <FilterBar
              severityFilter={correlationSeverityFilter}
              setSeverityFilter={setCorrelationSeverityFilter}
              typeFilter={correlationTypeFilter}
              setTypeFilter={setCorrelationTypeFilter}
              availableSeverities={getUniqueSeverities(insights.correlations)}
              availableTypes={getUniqueTypes(insights.correlations)}
              totalCount={insights.correlations?.length || 0}
              filteredCount={filteredCorrelations.length}
            />
            {filteredCorrelations.length > 0 ? (
              <div className="space-y-4">
                {filteredCorrelations.map((correlation, idx) => (
                  <MLInsightCard key={idx} insight={correlation} index={idx} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                {insights.correlations.length > 0 
                  ? 'No relationships match the selected filters. Try adjusting your filters.'
                  : 'No significant relationships detected in the data.'}
              </p>
            )}
          </div>
        );

      case 'anomalies':
        return (
          <div className="space-y-6">
            <SectionHeader
              title="Unusual Cases"
              count={filteredAnomalies.length}
              description="Welds or defects that stand out from typical patterns and may require special attention"
            />
            <FilterBar
              severityFilter={anomalySeverityFilter}
              setSeverityFilter={setAnomalySeverityFilter}
              typeFilter={anomalyTypeFilter}
              setTypeFilter={setAnomalyTypeFilter}
              availableSeverities={getUniqueSeverities(insights.anomalies)}
              availableTypes={getUniqueTypes(insights.anomalies)}
              totalCount={insights.anomalies?.length || 0}
              filteredCount={filteredAnomalies.length}
            />
            {filteredAnomalies.length > 0 ? (
              <div className="space-y-4">
                {filteredAnomalies.map((anomaly, idx) => (
                  <MLInsightCard key={idx} insight={anomaly} index={idx} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                {insights.anomalies.length > 0 
                  ? 'No unusual cases match the selected filters. Try adjusting your filters.'
                  : 'No unusual cases detected in the data.'}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="ml-insights-panel">
      {!hideNav ? (
        <InsightsSectionNav
          aria-label="AI NDT insights sections"
          tabs={sections.map((tab) => ({
            id: tab.id,
            label: tab.label,
            count: tab.count,
            badge: showAiBadges ? 'AI' : null,
          }))}
          activeId={activeSection}
          onChange={setActiveSection}
        />
      ) : null}

      <div>{renderSectionContent()}</div>
    </div>
  );
};

